import PropTypes from 'prop-types';
import React, {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import Slider from 'react-slick';
import { MAP_VIEW_CONFIG } from '../../brands/default/config';
import { generateUEID } from '../../helpers/generateID';
import ThemeContext from '../../providers/ThemeProvider';
import MapViewDeal from './MapViewDeal';

const MapDealsCarousel = forwardRef(
  (
    {
      deals,
      isMobile,
      onClose,
      setSelectedPin,
      selectedPinId,
      showClose = true,
    },
    ref,
  ) => {
    const slider = useRef();

    const slideIndexLookup = Object.fromEntries(
      deals.map((deal, index) => [deal.dealIid, index]),
    );

    useImperativeHandle(ref, () => ({
      setSlide(pinId) {
        const dealId = pinId.slice(0, pinId.indexOf('_'));
        slider.current.slickGoTo(slideIndexLookup[dealId], false);
      },
    }));

    function handleChange(newIndex) {
      const pinId = deals[newIndex] ? deals[newIndex].latLon[0].pinId : null;
      const dealId = deals[newIndex] ? deals[newIndex].dealIid : null;
      if (setSelectedPin) setSelectedPin(pinId, dealId);
    }

    const theme = useContext(ThemeContext);
    const primaryColorUrlencoded = encodeURIComponent(theme.colors.tertiary);
    const MAX_SLIDES = isMobile
      ? MAP_VIEW_CONFIG.MAP_MAX_CAROUSEL_MOBILE
      : MAP_VIEW_CONFIG.MAP_MAX_CAROUSEL;
    let numberOfSlides = 2;
    if (deals && deals.length === 1) numberOfSlides = 1;
    const settings = {
      adaptiveWidth: true,
      afterChange: handleChange,
      arrows: true,
      dots: false,
      infinite: true,
      initialSlide: 0,
      lazyLoad: true,
      responsive: [
        {
          breakpoint: 768,
          settings: {
            slidesToScroll: 1,
            slidesToShow: numberOfSlides,
          },
        },
        {
          breakpoint: 480,
          settings: {
            slidesToScroll: 1,
            slidesToShow: 1,
          },
        },
      ],
      slidesToScroll: 1,
      slidesToShow: numberOfSlides,
      // true doesn't work
      speed: 500,
      // vertical: true,
      // verticalScrolling: true,
      // verticalSwiping: true,
    };

    useEffect(() => {
      if (selectedPinId) {
        const dealId = selectedPinId.slice(0, selectedPinId.indexOf('_'));
        slider.current.slickGoTo(slideIndexLookup[dealId], false);
      }
    }, [selectedPinId, slideIndexLookup]);

    return (
      <>
        <div className="map-carousel-container">
          {showClose && (
            <button
              className="close-button"
              id="close-button"
              onClick={onClose}
              type="button"
            >
              × <span className="sr-only">Close</span>
            </button>
          )}
          <div key={generateUEID()}>
            <Slider {...settings} ref={slider}>
              {deals?.slice(0, MAX_SLIDES).map((deal) => {
                return (
                  <MapViewDeal deal={deal} isCarousel key={generateUEID()} />
                );
              })}
            </Slider>
          </div>
        </div>

        <style jsx>{`
          .map-carousel-container {
            width: calc(100vw - 5rem);
            height: auto;
            left: 2rem;
            bottom: 1rem;
            position: absolute;
            z-index: 999;
          }
          .close-button {
            background-color: ${theme.colors.background};
            border-radius: 50%;
            border-width: 0;
            box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.4);
            color: ${theme.colors.headingtext};
            font-size: 2.2rem;
            height: 30px;
            line-height: 2.4rem;
            margin-left: 20px;
            width: 30px;
            padding: 0;
            position: absolute;
            top: -35px;
            right: 0;
          }
          @media only screen and (min-width: ${theme.breakpoints.mdUp}) {
            .map-carousel-container {
              bottom: 6rem;
              max-width: ${numberOfSlides === 2 ? '640px' : '320px'};
              left: 5rem;
            }
          }
        `}</style>
        <style global jsx>{`
          .slick-list {
            padding-top: 2px;
            z-index: 9;
          }
          .slick-slide {
            // margin-left: 1px;
          }
          .slick-next,
          .slick-prev {
            z-index: 10;
            top: 48%;
            transform: translateY(-50%);
          }
          .slick-next {
            right: -22px;
          }
          .slick-prev {
            left: -22px;
          }
          @media only screen and (min-width: ${theme.breakpoints.mdUp}) {
            .slick-next {
              right: -22px;
            }
            .slick-prev {
              left: -35px;
            }
          }
          @media only screen and (min-width: ${theme.breakpoints.lgUp}) {
            .slick-next {
              right: -30px;
            }
            .slick-prev {
              left: -30px;
            }
          }
          .slick-next:before {
            background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 256 512'%3E%3Cpath fill='${primaryColorUrlencoded}' d='M224.3 273l-136 136c-9.4 9.4-24.6 9.4-33.9 0l-22.6-22.6c-9.4-9.4-9.4-24.6 0-33.9l96.4-96.4-96.4-96.4c-9.4-9.4-9.4-24.6 0-33.9L54.3 103c9.4-9.4 24.6-9.4 33.9 0l136 136c9.5 9.4 9.5 24.6.1 34z'/%3E%3C/svg%3E");
            background-repeat: no-repeat;
          }
          .slick-prev:before {
            background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 256 512'%3E%3Cpath fill='${primaryColorUrlencoded}' d='M31.7 239l136-136c9.4-9.4 24.6-9.4 33.9 0l22.6 22.6c9.4 9.4 9.4 24.6 0 33.9L127.9 256l96.4 96.4c9.4 9.4 9.4 24.6 0 33.9L201.7 409c-9.4 9.4-24.6 9.4-33.9 0l-136-136c-9.5-9.4-9.5-24.6-.1-34z'/%3E%3C/svg%3E");
            background-repeat: no-repeat;
          }
          .slick-next:before,
          .slick-prev:before {
            content: '';
            font-size: 15px;
            padding: 10px;
            display: flex;
            height: 30px;
            width: 30px;
            background-color: white;
            border-radius: 50%;
            background-position: 7.25px;
          }
        `}</style>
      </>
    );
  },
);

MapDealsCarousel.displayName = 'MapDealsCarousel';

export default React.memo(MapDealsCarousel);

/* const comparator = (prevProps, nextProps) => {
  if (prevProps.selectedPinId === nextProps.selectedPinId) {
    return true;
  }
  return false;
};

export default React.memo(MapDealsCarousel, comparator);*/

MapDealsCarousel.propTypes = {
  deals: PropTypes.array,
  isMobile: PropTypes.bool,
  onClose: PropTypes.func,
  selectedPinId: PropTypes.string,
  setSelectedPin: PropTypes.func,
  showClose: PropTypes.bool,
};

MapDealsCarousel.defaultProps = {
  deals: [],
  isMobile: true,
  onClose: () => {},
  selectedPinId: '',
  setSelectedPin: () => {},
  showClose: true,
};
